import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { BenefitDTOBenefitEnum, BulkActivationUpdateResponseDTO, UpdateBenefitActivationDTO } from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { useWithMessage } from 'probonio-shared-ui/module/error';
import { useTenantID } from 'probonio-shared-ui/module/me';
import { useRefetchEmployees } from '../../userManagement/query';
import { useRefetchActivations } from '../query';
import { BulkPauseFailedError, PauseMutationDTO } from './useBulkPauseActivationsMutation';

export function useSingleUpdateActivationMutation(employeeId: string, activationId: string) {
  const getTenantId = useTenantID();
  const withMessage = useWithMessage();
  const refetchActivations = useRefetchActivations(employeeId);

  return useMutation({
    mutationFn: async (update: UpdateBenefitActivationDTO): Promise<BulkActivationUpdateResponseDTO> => {
      const result = await apis.benefits
        .updateBenefitActivation({
          tenantId: getTenantId(),
          employeeId,
          benefitActivationId: activationId,
          updateBenefitActivationDTO: update,
        })
        .then(res => res.data);
      return {
        results: [{ status: result.adjustedEndDate ? 'ADJUSTMENT' : 'SUCCESS', endDate: result.adjustedEndDate }],
      };
    },
    onError: (err: Error) => {
      withMessage(err);
    },
    onSuccess: async () => {
      await refetchActivations();
    },
  });
}

export function useSinglePauseActivationMutation(benefit: BenefitDTOBenefitEnum, employeeId: string, customBenefitId?: string) {
  const getTenantId = useTenantID();
  const withMessage = useWithMessage();
  const refetchEmployees = useRefetchEmployees();
  const refetchActivations = useRefetchActivations(employeeId);

  return useMutation({
    mutationFn: async (pauseDTO: PauseMutationDTO): Promise<BulkActivationUpdateResponseDTO> => {
      try {
        return await apis.benefits
          .pauseBenefits({
            tenantId: getTenantId(),
            pauseBenefitActivationsDTO: {
              benefit,
              customBenefitId,
              employeeIds: [employeeId],
              endCurrentActivation: pauseDTO.endCurrentActivation,
              startNewActivation: pauseDTO.startNewActivation,
            },
          })
          .then(res => res.data);
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 422) {
          throw BulkPauseFailedError.fromResponse(err.response.data);
        }
        throw err;
      }
    },
    onError: (err: Error) => {
      if (!(err instanceof BulkPauseFailedError)) {
        withMessage(err);
      }
    },
    onSuccess: async () => {
      await Promise.all([refetchEmployees(), refetchActivations()]);
    },
  });
}
